/**
 * HexArt - Main entry point
 *
 * Hexagonal art canvas with Three.js
 */

import { HexScene, PALETTE, MATERIALS } from './HexScene'
import { soundManager } from './SoundManager'

// Initialize scene
const container = document.getElementById('app')!
const scene = new HexScene(container)

// Sound state
let soundEnabled = true
const soundToggle = document.getElementById('sound-toggle')!

// Initialize audio on first user interaction
const initAudio = async () => {
  await soundManager.init()
  soundManager.play('intro')
  document.removeEventListener('click', initAudio)
  document.removeEventListener('keydown', initAudio)
}
document.addEventListener('click', initAudio)
document.addEventListener('keydown', initAudio)

// Sound toggle
soundToggle.addEventListener('click', (e) => {
  e.stopPropagation()
  soundEnabled = !soundEnabled
  soundManager.setEnabled(soundEnabled)
  soundToggle.textContent = soundEnabled ? '🔊' : '🔇'
})

// ============================================================================
// Brush Size UI
// ============================================================================

const brushValueEl = document.getElementById('brush-value')!
const brushHexCountEl = document.getElementById('brush-hex-count')!
const brushDecBtn = document.getElementById('brush-dec')!
const brushIncBtn = document.getElementById('brush-inc')!

// Calculate hex count for brush size (1 + 6 + 12 + 18 + ... = 1 + 6*(1+2+...+(n-1)))
function getHexCount(brushSize: number): number {
  if (brushSize === 1) return 1
  // Sum of hexes in filled circle of radius (brushSize-1)
  // = 1 + 6*1 + 6*2 + ... + 6*(brushSize-1)
  // = 1 + 6 * (1+2+...+(brushSize-1))
  // = 1 + 6 * (brushSize-1)*brushSize/2
  // = 1 + 3 * brushSize * (brushSize-1)
  return 1 + 3 * brushSize * (brushSize - 1)
}

function updateBrushUI(size: number) {
  brushValueEl.textContent = String(size)
  const count = getHexCount(size)
  brushHexCountEl.textContent = `(${count} hex${count > 1 ? 'es' : ''})`
}

// Wire up brush size buttons
brushDecBtn.addEventListener('click', (e) => {
  e.stopPropagation()
  scene.setBrushSize(scene.brushSize - 1)
  soundManager.playBrushSize(scene.brushSize, 10)
})

brushIncBtn.addEventListener('click', (e) => {
  e.stopPropagation()
  scene.setBrushSize(scene.brushSize + 1)
  soundManager.playBrushSize(scene.brushSize, 10)
})

// Listen for brush size changes from scene
scene.onBrushSizeChange = updateBrushUI

// ============================================================================
// Symmetry UI
// ============================================================================

const symmetryValueEl = document.getElementById('symmetry-value')!

function updateSymmetryUI(mode: number) {
  if (mode === 0) {
    symmetryValueEl.textContent = 'Off'
    symmetryValueEl.style.color = 'rgba(255, 255, 255, 0.5)'
  } else {
    symmetryValueEl.textContent = `×${mode}`
    symmetryValueEl.style.color = '#a78bfa'
  }
}

scene.onSymmetryChange = updateSymmetryUI

// Symmetry button
const symmetryBtn = document.getElementById('symmetry-btn')!
symmetryBtn.addEventListener('click', (e) => {
  e.stopPropagation()
  scene.cycleSymmetry()
  soundManager.play('click')
})

// ============================================================================
// Material Palette UI
// ============================================================================

const MATERIAL_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', 'z', 'x', 'c', 'v', 'b', 'n', 'm', ',']

const paletteEl = document.getElementById('palette')!
let activeColorBtn: HTMLElement | null = null
const colorButtons: HTMLElement[] = []

MATERIALS.forEach((material, index) => {
  const btn = document.createElement('button')
  btn.className = 'color-btn'
  btn.style.backgroundColor = `#${material.color.toString(16).padStart(6, '0')}`
  btn.title = material.name

  // Special styling for transparent materials
  if (material.transparent) {
    btn.style.border = '2px solid rgba(255, 255, 255, 0.3)'
  }

  // Add glow for emissive materials
  if (material.emissiveIntensity > 0.3) {
    btn.style.boxShadow = `0 0 10px #${material.emissive.toString(16).padStart(6, '0')}`
  }

  // Add key hint
  if (index < MATERIAL_KEYS.length) {
    const keyHint = document.createElement('span')
    keyHint.className = 'key-hint'
    keyHint.textContent = MATERIAL_KEYS[index]
    btn.appendChild(keyHint)
  }

  if (index === 0) {
    btn.classList.add('active')
    activeColorBtn = btn
  }

  btn.addEventListener('click', () => {
    selectMaterial(index)
  })

  paletteEl.appendChild(btn)
  colorButtons.push(btn)
})

function selectMaterial(index: number) {
  if (index < 0 || index >= MATERIALS.length) return

  scene.setMaterial(index)
  activeColorBtn?.classList.remove('active')
  colorButtons[index].classList.add('active')
  activeColorBtn = colorButtons[index]

  soundManager.playColorSwitch(index, MATERIALS.length)

  // Update material name display
  const materialNameEl = document.getElementById('material-name')
  if (materialNameEl) {
    materialNameEl.textContent = MATERIALS[index].name
  }

  // Update FPS hotbar material display
  const fpsMat = document.getElementById('fps-current-material')
  if (fpsMat) {
    fpsMat.style.backgroundColor = `#${MATERIALS[index].color.toString(16).padStart(6, '0')}`
    if (MATERIALS[index].emissiveIntensity > 0.3) {
      fpsMat.style.boxShadow = `0 0 10px #${MATERIALS[index].emissive.toString(16).padStart(6, '0')}`
    } else {
      fpsMat.style.boxShadow = 'none'
    }
  }
}

// ============================================================================
// Keyboard Shortcuts
// ============================================================================

document.addEventListener('keydown', (e) => {
  // Ignore if in input
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

  const key = e.key.toUpperCase()

  // In FPS mode, certain keys are handled differently
  if (scene.isFPSMode) {
    // Only allow escape and limited keys in FPS mode
    if (e.key === 'Escape') {
      e.preventDefault()
      scene.toggleFPSMode()
      updateFPSUI(false)
      return
    }
    // Block other keybinds when in FPS mode (WASD handled in scene)
    if (['W', 'A', 'S', 'D'].includes(key) || e.key === ' ') {
      return
    }
  }

  // Brush size controls (Q/E)
  if (key === 'Q') {
    e.preventDefault()
    scene.setBrushSize(scene.brushSize - 1)
    soundManager.playBrushSize(scene.brushSize, 10)
    return
  }
  if (key === 'E') {
    e.preventDefault()
    scene.setBrushSize(scene.brushSize + 1)
    soundManager.playBrushSize(scene.brushSize, 10)
    return
  }

  // Material selection (1-9, 0, -, =, p)
  const materialIndex = MATERIAL_KEYS.indexOf(e.key)
  if (materialIndex !== -1 && materialIndex < MATERIALS.length) {
    e.preventDefault()
    selectMaterial(materialIndex)
    return
  }

  // Toggle sound
  if (key === 'M') {
    e.preventDefault()
    soundEnabled = !soundEnabled
    soundManager.setEnabled(soundEnabled)
    soundToggle.textContent = soundEnabled ? '🔊' : '🔇'
    return
  }

  // Cycle symmetry mode
  if (key === 'S') {
    e.preventDefault()
    scene.cycleSymmetry()
    soundManager.play('click')
    return
  }

  // Clear all with Backspace or Delete
  if (e.key === 'Backspace' || e.key === 'Delete') {
    e.preventDefault()
    scene.clearAll()
    soundManager.play('clear')
    return
  }

  // Game of Life controls
  if (e.key === ' ') {
    e.preventDefault()
    scene.golStep()
    soundManager.play('click')
    return
  }
  if (key === 'G') {
    e.preventDefault()
    const running = scene.golToggleRun()
    updateGolUI(running)
    soundManager.play(running ? 'pattern' : 'click')
    return
  }

  // Gravity
  if (key === 'V') {
    e.preventDefault()
    scene.gravityToggle()
    soundManager.play('click')
    return
  }

  // Toggle 3D mode
  if (key === 'T') {
    e.preventDefault()
    const is3D = scene.toggle3DMode()
    update3DUI(is3D)
    soundManager.play('click')
    return
  }

  // Toggle FPS mode
  if (key === 'F') {
    e.preventDefault()
    const isFPS = scene.toggleFPSMode()
    updateFPSUI(isFPS)
    soundManager.play('click')
    return
  }
})

// ============================================================================
// Game of Life UI
// ============================================================================

const golIndicator = document.getElementById('gol-indicator')!

function updateGolUI(running: boolean) {
  golIndicator.style.display = running ? 'block' : 'none'
}

// ============================================================================
// 3D Mode UI
// ============================================================================

const mode3DIndicator = document.getElementById('mode-3d')!

function update3DUI(is3D: boolean) {
  mode3DIndicator.textContent = is3D ? '3D' : '2D'
  mode3DIndicator.style.color = is3D ? '#4ade80' : 'rgba(255, 255, 255, 0.5)'
}

// ============================================================================
// FPS Mode UI
// ============================================================================

const fpsIndicator = document.getElementById('fps-indicator')!
const crosshair = document.getElementById('crosshair')!
const fpsHotbar = document.getElementById('fps-hotbar')!
const miningProgress = document.getElementById('mining-progress')!
const miningProgressBar = document.getElementById('mining-progress-bar')!
const fpsModeTitle = document.getElementById('fps-mode-title')!
const fpsCurrentMaterial = document.getElementById('fps-current-material')!
const hotbarSlots = document.querySelectorAll('.hotbar-slot')

function updateFPSUI(isFPS: boolean) {
  if (fpsIndicator) {
    fpsIndicator.style.display = isFPS ? 'block' : 'none'
  }
  if (crosshair) {
    crosshair.style.display = isFPS ? 'block' : 'none'
  }
  if (fpsHotbar) {
    fpsHotbar.style.display = isFPS ? 'flex' : 'none'
    if (isFPS) updateFPSHotbarMaterial()
  }
  if (miningProgress) {
    miningProgress.style.display = 'none'
  }
  // Hide/show orbit UI elements
  const keybindHelper = document.getElementById('keybind-helper')
  const brushSize = document.getElementById('brush-size')
  const palette = document.getElementById('palette')
  const materialName = document.getElementById('material-name')
  if (keybindHelper) keybindHelper.style.display = isFPS ? 'none' : 'block'
  if (brushSize) brushSize.style.display = isFPS ? 'none' : 'flex'
  if (palette) palette.style.display = isFPS ? 'none' : 'flex'
  if (materialName) materialName.style.display = isFPS ? 'none' : 'block'

  // Update mode indicator
  updateFlyModeUI()
}

function updateFlyModeUI() {
  if (fpsModeTitle) {
    fpsModeTitle.textContent = scene.flyMode ? 'FLY MODE' : 'WALK MODE'
    fpsModeTitle.style.color = scene.flyMode ? '#60a5fa' : '#4ade80'
  }
}

function updateFPSHotbarMaterial() {
  if (fpsCurrentMaterial) {
    const material = MATERIALS[colorButtons.indexOf(activeColorBtn!)] || MATERIALS[0]
    fpsCurrentMaterial.style.backgroundColor = `#${material.color.toString(16).padStart(6, '0')}`
    if (material.emissiveIntensity > 0.3) {
      fpsCurrentMaterial.style.boxShadow = `0 0 10px #${material.emissive.toString(16).padStart(6, '0')}`
    } else {
      fpsCurrentMaterial.style.boxShadow = 'none'
    }
  }
}

// Hotbar click handlers
hotbarSlots.forEach(slot => {
  slot.addEventListener('click', () => {
    const tool = (slot as HTMLElement).dataset.tool as 'hand' | 'pickaxe' | 'shovel' | 'axe'
    if (tool) {
      scene.setTool(tool)
      soundManager.play('click')
    }
  })
})

// Mining progress callback
scene.onMiningProgress = (progress, _target) => {
  if (progress > 0) {
    miningProgress.style.display = 'block'
    miningProgressBar.style.width = `${progress * 100}%`
  } else {
    miningProgress.style.display = 'none'
    miningProgressBar.style.width = '0%'
  }
}

// Tool change callback
scene.onToolChange = (tool) => {
  hotbarSlots.forEach(slot => {
    const slotTool = (slot as HTMLElement).dataset.tool
    slot.classList.toggle('active', slotTool === tool)
  })
}

// Fly mode change callback
scene.onFlyModeChange = (_isFly) => {
  updateFlyModeUI()
}

// ============================================================================
// World Selector
// ============================================================================

const worldButtons = document.querySelectorAll('.world-btn')
worldButtons.forEach(btn => {
  btn.addEventListener('click', (e) => {
    e.stopPropagation()
    const worldName = (btn as HTMLElement).dataset.world
    if (worldName) {
      scene.loadWorld(worldName)
      soundManager.play('pattern')
      // Update time slider to match world's time setting
      updateTimeUI()
    }
  })
})

// ============================================================================
// Time Control
// ============================================================================

const timeSlider = document.getElementById('time-slider') as HTMLInputElement
const timeIcon = document.getElementById('time-icon')!
const timeLabel = document.getElementById('time-label')!
const timeAutoCheckbox = document.getElementById('time-auto') as HTMLInputElement

function getTimeInfo(t: number): { icon: string; label: string } {
  if (t < 0.2) return { icon: '🌙', label: 'Night' }
  if (t < 0.3) return { icon: '🌅', label: 'Dawn' }
  if (t < 0.4) return { icon: '☀️', label: 'Morning' }
  if (t < 0.6) return { icon: '🌞', label: 'Noon' }
  if (t < 0.7) return { icon: '☀️', label: 'Afternoon' }
  if (t < 0.8) return { icon: '🌅', label: 'Sunset' }
  if (t < 0.9) return { icon: '🌆', label: 'Dusk' }
  return { icon: '🌙', label: 'Night' }
}

function updateTimeUI() {
  const t = scene.getTimeOfDay()
  timeSlider.value = String(t * 100)
  const info = getTimeInfo(t)
  timeIcon.textContent = info.icon
  timeLabel.textContent = info.label
  timeAutoCheckbox.checked = scene.autoTime
}

timeSlider.addEventListener('input', () => {
  const t = parseInt(timeSlider.value) / 100
  scene.setTimeOfDay(t)
  scene.autoTime = false
  timeAutoCheckbox.checked = false
  const info = getTimeInfo(t)
  timeIcon.textContent = info.icon
  timeLabel.textContent = info.label
})

timeAutoCheckbox.addEventListener('change', () => {
  scene.autoTime = timeAutoCheckbox.checked
})

// Update time UI periodically
setInterval(() => {
  if (scene.autoTime) {
    updateTimeUI()
  }
}, 500)

// ============================================================================
// Start
// ============================================================================

scene.start()

// Expose to console for fun experiments
;(window as any).hexart = scene
;(window as any).sound = soundManager
console.log('HexArt loaded! Access scene with `hexart`, sound with `sound`')
console.log('Brush: Q/E to change size, click+drag to paint!')
